import { useCallback, useEffect, useState } from 'react';
import { deactivateDevice, listDevices, type DeviceListItem } from '../api/devices';
import { ApiError, TimeoutError } from '../api/client';
import { capabilitiesSummary, platformLabel, relativeTime, sortDevices } from './devicesView';

/// Settings → Devices card. Lists every active native app registration
/// (`GET /account/devices`) and offers a per-device "Sign out" action
/// (`DELETE /account/devices/{id}`) that soft-deactivates the row and
/// fires a `device_removed` silent push at the affected device. The
/// current device is marked with a "This device" badge and its
/// Sign Out button is hidden — logging out of *this* device is what
/// the header's Sign Out button is for.
export default function Devices() {
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pending, setPending] = useState<DeviceListItem | null>(null);
  const [signOutInFlight, setSignOutInFlight] = useState<Set<string>>(new Set());
  const [signOutError, setSignOutError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const items = await listDevices();
      setDevices(sortDevices(items));
    } catch (err) {
      setLoadError(messageFromError(err, 'Failed to load devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function confirmSignOut(device: DeviceListItem) {
    setPending(null);
    setSignOutInFlight((prev) => {
      const next = new Set(prev);
      next.add(device.id);
      return next;
    });
    setSignOutError('');
    try {
      await deactivateDevice(device.id);
      // Optimistically drop the row — the server has already
      // soft-deactivated and the next reload would do the same.
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } catch (err) {
      setSignOutError(messageFromError(err, 'Failed to sign out device.'));
    } finally {
      setSignOutInFlight((prev) => {
        const next = new Set(prev);
        next.delete(device.id);
        return next;
      });
    }
  }

  return (
    <section className="card devices-section">
      <div className="devices-header">
        <h2>Devices</h2>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => void reload()}
          disabled={loading}
          aria-label="Refresh devices"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p className="devices-blurb">
        Meridian app installations registered to your account. This is not a
        browser-session list. Revoke anything you don't recognize — that
        installation will be wiped on its next wake.
      </p>

      {loadError && (
        <div className="error-message" role="alert">
          {loadError}
        </div>
      )}
      {signOutError && (
        <div className="error-message" role="alert">
          {signOutError}
        </div>
      )}

      {loading && devices.length === 0 ? (
        <p className="devices-loading">Loading devices…</p>
      ) : devices.length === 0 ? (
        <p className="devices-empty">No devices to show.</p>
      ) : (
        <ul className="devices-list" aria-label="Devices">
          {devices.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              signingOut={signOutInFlight.has(d.id)}
              onSignOut={() => setPending(d)}
            />
          ))}
        </ul>
      )}

      {pending && (
        <ConfirmDialog
          device={pending}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmSignOut(pending)}
        />
      )}
    </section>
  );
}

interface DeviceRowProps {
  device: DeviceListItem;
  signingOut: boolean;
  onSignOut: () => void;
}

function DeviceRow({ device, signingOut, onSignOut }: DeviceRowProps) {
  const lastSyncLabel = device.last_sync_at
    ? `Last synced ${relativeTime(device.last_sync_at)}`
    : "Hasn't synced yet";
  const isCurrent = device.current === true;
  const hardware = capabilitiesSummary(device.attributes);
  return (
    <li className="device-row">
      <div className="device-row-main">
        <div className="device-row-line">
          <span className="device-name">{device.name}</span>
          <span className="device-platform">{platformLabel(device.device_type)}</span>
          {isCurrent && (
            <span className="device-badge device-badge-current">This device</span>
          )}
          {device.paired && (
            <span className="device-badge device-badge-paired">Paired</span>
          )}
          {device.push_platform && (
            <span className="device-badge device-badge-push">Push</span>
          )}
        </div>
        <div className="device-row-meta">
          {hardware && <span className="device-hardware">{hardware}</span>}
          <span>First seen {relativeTime(device.created_at)}</span>
          <span>{lastSyncLabel}</span>
        </div>
      </div>
      {!isCurrent && (
        <button
          type="button"
          className="btn btn-danger btn-sm"
          onClick={onSignOut}
          disabled={signingOut}
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      )}
    </li>
  );
}

interface ConfirmDialogProps {
  device: DeviceListItem;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDialog({ device, onCancel, onConfirm }: ConfirmDialogProps) {
  return (
    <div
      className="device-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="device-confirm-title"
    >
      <div className="device-confirm-card">
        <h3 id="device-confirm-title">Sign out this device?</h3>
        <p>
          <strong>{device.name}</strong> will be signed out and its local
          data wiped on its next wake. You can sign it back in any time
          by re-registering it.
        </p>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            autoFocus
          >
            Sign out
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function messageFromError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof TimeoutError) return 'Request timed out. Please try again.';
  return fallback;
}
